// Menu.jsx — adds scroll-aware background & makes off-canvas dark.
// • Initial navbar background: 60% black (glass) → solid black after first scroll.
// • Offcanvas gets forced mainColor_black.

// React
import { useContext, useMemo, useEffect, useState, useCallback, forwardRef, useRef } from 'react';
import {
  Navbar,
  Nav,
  Offcanvas,
  Container,
} from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

// Context
import { mainContext } from '../../context/mainContext';

// Components
import ServicesSelector from '../shared/ServicesSelector';
import LanguageSelector from '../shared/LanguageSelector'

// Assets
import logo from '../../assets/images/logo.png';
import '../../assets/styles/menu.css';

// Hooks
import { useTranslation } from '../../hooks/useTranslation';

export default function Menu() {

  const ActiveHashLink = forwardRef(function ActiveHashLink(props, ref) {
    const { pathname, hash } = useLocation();
    const isActive = pathname + hash === props.to;

    const className =
      typeof props.className === 'function'
        ? props.className({ isActive })        // call the same helper
        : props.className;

    return <HashLink ref={ref} {...props} className={className} />;
  });

  const linkClass = ({ isActive }) =>
    `nav-link menu-link${isActive ? ' active_link' : ''}`;

  const BREAKPOINT = 'xxl'; // 1400 px

  /* ---------- context & routing ---------- */
  const { setNavActive } = useContext(mainContext);
  const t = useTranslation();

  /* ---------- scroll background ---------- */
  const [scrolled, setScrolled] = useState(false);


  /* ---------- drawer state (QoL) ---------- */
  const [showDrawer, setShowDrawer] = useState(false);

  const hideDrawer = useCallback(() => setShowDrawer(false), []);

  const toggleDrawer = useCallback(
    () => setShowDrawer((s) => !s),
    [],
  );

  const brandRef = useRef(null);
  const [brandWidth, setBrandWidth] = useState(0);

  useEffect(() => {
    if (!brandRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setBrandWidth(entry.contentRect.width);
    });

    observer.observe(brandRef.current);

    return () => observer.disconnect();
  }, []);

  // Scrollbar szélességének meghatározása (egyszer)
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useEffect(() => {
    const getScrollbarWidth = () => {
      const outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.overflow = 'scroll';
      outer.style.msOverflowStyle = 'scrollbar';
      outer.style.width = '100px';
      outer.style.height = '100px';

      document.body.appendChild(outer);

      const inner = document.createElement('div');
      inner.style.width = '100%';
      inner.style.height = '100%';
      outer.appendChild(inner);

      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      document.body.removeChild(outer);

      return scrollbarWidth;
    };

    const width = getScrollbarWidth();
    setScrollbarWidth(width);
  }, []);

  /* 🚫 hide scrollbar while drawer is open */
  useEffect(() => {
    function preventScroll(e) {
      const drawer = document.getElementById('main-offcanvas');

      if (!drawer) {
        e.preventDefault();
        return;
      }

      // Ellenőrizd az EGÉSZ offcanvas-t, ne csak a body-t
      if (drawer.contains(e.target)) {
        // További ellenőrzés: csak az offcanvas-body-ban engedélyezd
        const offcanvasBody = drawer.querySelector('.offcanvas-body');
        if (offcanvasBody && offcanvasBody.contains(e.target)) {
          return; // Engedélyezi a scroll-t
        }
      }

      e.preventDefault(); // Mindenütt máshol tiltja
    }

    const applyPaddingToElements = (paddingValue) => {
      const elements = ['nav', 'main', 'footer'];
      elements.forEach(tagName => {
        const element = document.querySelector(tagName);
        if (element) {
          if (paddingValue) {
            element.style.paddingRight = paddingValue;
          } else {
            element.style.removeProperty('padding-right');
          }
        }
      });
    };

    if (showDrawer) {
      applyPaddingToElements(`${scrollbarWidth}px`);
      document.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('wheel', preventScroll, { passive: false });

      const style = document.createElement('style');
      style.id = 'drawer-scroll-lock';
      style.textContent = `
      body { overflow: hidden !important; }
      #main-offcanvas {
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }
      `;
      document.head.appendChild(style);

    } else {
      document.removeEventListener('touchmove', preventScroll, { passive: false });
      document.removeEventListener('wheel', preventScroll, { passive: false });

      const style = document.getElementById('drawer-scroll-lock');
      if (style) {
        style.remove();
      }

      const style2 = document.createElement('style');
      style2.id = 'drawer-scroll-lock';
      style2.textContent = `
          body { overflow: auto !important; }
        `;
      document.head.appendChild(style2);

      applyPaddingToElements(null);
    }

    return () => {
      document.removeEventListener('touchmove', preventScroll, { passive: false });
      document.removeEventListener('wheel', preventScroll, { passive: false });

      const style = document.getElementById('drawer-scroll-lock');
      if (style) {
        style.remove();
      }
    };
  }, [showDrawer, scrollbarWidth]);
  /* 🚫 hide scrollbar while drawer is open */

  /*Smth other*/
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ---------- nav-item config ---------- */
  const navItems = useMemo(
    () => [
      { id: 'szolgaltatasok', label: <ServicesSelector setNavActive={setNavActive} hideDrawer={hideDrawer} />, to: '/szolgaltatasok', component: NavLink, setActive: () => setNavActive('szolgaltatasok') },
      { id: 'arlista', label: t('nav.pricelist'), to: '/arlista', component: NavLink, setActive: () => setNavActive('arlista') },
      { id: 'edzok', label: t('nav.trainers'), to: '/edzok', component: NavLink, setActive: () => setNavActive('edzok') },
      { id: 'orarend', label: t('nav.timetable'), to: '/orarend', component: NavLink, setActive: () => setNavActive('orarend') },
      { id: 'hirek', label: t('nav.news'), to: '/hirek', component: NavLink, setActive: () => setNavActive('hirek') },
      { id: 'galeria', label: t('nav.gallery'), to: '/galeria', component: NavLink, setActive: () => setNavActive('galeria') },
      { id: 'rolunk', label: t('nav.about'), to: '/rolunk', component: NavLink, setActive: () => setNavActive('rolunk') },
      { id: 'karrier', label: t('nav.career'), to: '/karrier', component: NavLink, setActive: () => setNavActive('karrier') },
      {
        id: 'kapcsolat',
        label: t('nav.contact'),
        to: '#kapcsolat',
        component: ActiveHashLink,
        setActive: () => setNavActive('kapcsolat')
      },
    ],
    [t, setNavActive, ActiveHashLink, hideDrawer],
  );

  const renderNavItems = orientation => (
    <Nav as="ul" className={`menu-navList flex-${orientation}`} role="menu">
      {navItems.map(({ id, label, to, component: LinkComp }) => {
        const common = {
          to,
          role: 'menuitem',
          className: linkClass,
          onClick: hideDrawer,
        };

        // add `end` only when the component really is NavLink
        if (LinkComp === NavLink) common.end = true;

        return (
          <Nav.Item as="li" key={id} role="none">
            <LinkComp {...common}>{label}</LinkComp>
          </Nav.Item>
        );
      })}
    </Nav>
  );

  return (
    <Navbar
      expand={BREAKPOINT}
      fixed="top"
      data-bs-theme="dark"
      className={`menu-navbar ${scrolled ? 'menu-solid' : 'menu-glass'}`}
    >
      <Container fluid className="px-3 align-items-center position-relative">
        {/* Logo */}
        <Navbar.Brand
          ref={brandRef}
          as={NavLink}
          to="/"
          aria-label="Home"
          className="order-1 nav-side"
          onClick={() => setNavActive('rolunk')}
        >
          <img src={logo} alt="Gym logo" className="menu-logo" />
        </Navbar.Brand>

        {/* Language icon – mobile */}
        <div className="d-inline-flex d-xxl-none order-2 languageSelectorDiv">
          <LanguageSelector />
        </div>

        {/* Hamburger */}
        <Navbar.Toggle
          onClick={toggleDrawer}
          aria-controls="main-offcanvas"
          className="order-3 d-xxl-none nav-side"
          style={{ minWidth: `${brandWidth}px`, maxWidth: `${brandWidth}px` }}
        />

        {/* Desktop links */}
        <Navbar.Collapse id="main-collapse" className="d-none d-xxl-flex position-absolute start-50 translate-middle-x">
          {renderNavItems('row')}
        </Navbar.Collapse>

        {/* Offcanvas (mobile) */}
        <Navbar.Offcanvas
          id="main-offcanvas"
          placement="end"
          aria-label="Main navigation"
          className="menu-offcanvas d-xxl-none"
          restoreFocus={false}
          show={showDrawer}
          onHide={hideDrawer}
          responsive={`${BREAKPOINT}`}
        >
          <Offcanvas.Header
            closeButton
            data-bs-theme="dark"
            closeLabel="Close menu"
          />
          <Offcanvas.Body className="d-flex flex-column">
            {renderNavItems('column')}
          </Offcanvas.Body>
        </Navbar.Offcanvas>

        {/* Language icon – desktop */}
        <div className="d-none d-xxl-flex order-xxl-3 ms-xxl-3">
          <LanguageSelector />
        </div>
      </Container>
    </Navbar>
  );
}
